import {
  Bookmark,
  ChevronRight,
  CircleHelp,
  Compass,
  Gamepad2,
  History,
  List,
  LogIn,
  Settings,
  Sparkles,
  Trophy,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef } from "react";
import type { NavigationTab } from "../BottomNavigation/BottomNavigation";
import styles from "./SideMenu.module.css";

export type SecondaryPage = "saved" | "history" | "settings";

interface SideMenuProps {
  activePage: SecondaryPage | null;
  activeTab: NavigationTab;
  onClose: () => void;
  onNavigate: (tab: NavigationTab) => void;
  onOpenPage: (page: SecondaryPage) => void;
  onShowMessage: (message: string) => void;
}

interface MenuItem {
  id: NavigationTab;
  label: string;
  description: string;
  icon: LucideIcon;
}

const mainItems: readonly MenuItem[] = [
  {
    id: "games",
    label: "Spel",
    description: "Quests, nivåer och hela spelvalvet",
    icon: Gamepad2,
  },
  {
    id: "discover",
    label: "Upptäck",
    description: "Kvällens favoriter och rekommendationer",
    icon: Compass,
  },
  {
    id: "activity",
    label: "Aktivitet",
    description: "Din progress, XP och utmärkelser",
    icon: List,
  },
  {
    id: "friends",
    label: "Vänner",
    description: "Samla och synka ditt party crew",
    icon: Users,
  },
];

export function SideMenu({
  activePage,
  activeTab,
  onClose,
  onNavigate,
  onOpenPage,
  onShowMessage,
}: SideMenuProps) {
  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) {
        return;
      }

      const focusableElements = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements.at(-1);

      if (
        event.shiftKey &&
        (document.activeElement === firstElement ||
          !panelRef.current.contains(document.activeElement))
      ) {
        event.preventDefault();
        lastElement?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === lastElement ||
          !panelRef.current.contains(document.activeElement))
      ) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleNavigate = (tab: NavigationTab) => {
    onNavigate(tab);
    onClose();
  };

  const handleOpenPage = (page: SecondaryPage) => {
    onOpenPage(page);
    onClose();
  };

  return (
    <div className={styles.layer}>
      <button
        className={styles.scrim}
        type="button"
        aria-label="Stäng meny"
        tabIndex={-1}
        onClick={onClose}
      />
      <aside
        className={styles.panel}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="menu-heading"
      >
        <header className={styles.header}>
          <div className={styles.brand} aria-label="PartyCast">
            <span>
              <Sparkles aria-hidden="true" />
            </span>
            <div>
              <strong>PartyCast</strong>
              <small>Spela tillsammans</small>
            </div>
          </div>
          <button
            className={styles.closeButton}
            ref={closeButtonRef}
            type="button"
            aria-label="Stäng meny"
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </header>

        <div className={styles.profileCard}>
          <span className={styles.avatar}>G</span>
          <span className={styles.profileCopy}>
            <small>Spelar som</small>
            <strong>Gäst · Nivå 1</strong>
          </span>
          <span className={styles.xpBadge}>
            <Trophy aria-hidden="true" />
            0 XP
          </span>
          <button type="button" onClick={() => handleNavigate("friends")}>
            <LogIn aria-hidden="true" />
            Logga in och spara progress
            <ChevronRight aria-hidden="true" />
          </button>
        </div>

        <nav className={styles.menuNav} aria-labelledby="menu-heading">
          <h2 id="menu-heading">Meny</h2>
          {mainItems.map((item) => {
            const Icon = item.icon;
            const isActive = activePage === null && item.id === activeTab;

            return (
              <button
                type="button"
                key={item.id}
                aria-current={isActive ? "page" : undefined}
                onClick={() => handleNavigate(item.id)}
              >
                <span className={styles.menuIcon}>
                  <Icon aria-hidden="true" />
                </span>
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
                <ChevronRight aria-hidden="true" />
              </button>
            );
          })}
        </nav>

        <div className={styles.quickLinks}>
          <button
            type="button"
            aria-current={activePage === "saved" ? "page" : undefined}
            onClick={() => handleOpenPage("saved")}
          >
            <Bookmark aria-hidden="true" />
            Sparade spel
            <ChevronRight aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-current={activePage === "history" ? "page" : undefined}
            onClick={() => handleOpenPage("history")}
          >
            <History aria-hidden="true" />
            Spelhistorik
            <ChevronRight aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-current={activePage === "settings" ? "page" : undefined}
            onClick={() => handleOpenPage("settings")}
          >
            <Settings aria-hidden="true" />
            Inställningar
            <ChevronRight aria-hidden="true" />
          </button>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            onClick={() => {
              onShowMessage(
                "Hjälpcentret öppnas när supportsidan är ansluten.",
              );
              onClose();
            }}
          >
            <CircleHelp aria-hidden="true" />
            Hjälp & feedback
          </button>
          <span>PartyCast · Beta</span>
        </footer>
      </aside>
    </div>
  );
}
